// Forum gamification: the shared pieces (level emblem, EXP bar, game chip, medals with their tooltip) used by the post
// strip, the user card, the Progress page and the Leaderboard (owner 2026-11-04: "gamification look so good, do it";
// mockups in /root/aethernal2-work/handoff/forum-gamify). Data: the API user field `aetGamify` (src/Gamify/Present.php)
// and the forum attributes aetRates / aetBadgeStyle. Strings: locale "gamify.*" (generated from the website's
// forum.gamify.* by web/test/unit/forum-assets.test.ts).
import app from 'flarum/forum/app';
export const t = (key, params) => app.translator.trans(`aethernal-forum.forum.gamify.${key}`, params);
/** A plain-string translation (for attributes like aria-label / title). */
export const ts = (key, params) => {
  const v = t(key, params);
  return Array.isArray(v) ? v.join('') : String(v);
};
export const site = () => app.forum.attribute('aethernalSiteUrl') || 'https://aethernal2.eu';
export const nf = n => Number(n || 0).toLocaleString(app.data.locale || 'en');

/** The achievements in display order (src/Gamify/Achievements.php; Helping Hand only with a best-answer feature). */
export const ACH = {
  first: ['fas fa-feather-pointed', 'common', 'forum', 10],
  guide: ['fas fa-book-open', 'rare', 'forum', 50],
  scribe: ['fas fa-scroll', 'epic', 'forum', 100],
  like100: ['fas fa-heart', 'rare', 'forum', 25],
  like1k: ['fas fa-heart-circle-check', 'epic', 'forum', 100],
  streak: ['fas fa-fire', 'rare', 'forum', 25],
  veteran: ['fas fa-hourglass-half', 'common', 'forum', 25],
  lv75: ['fas fa-star-half-stroke', 'common', 'game', 10],
  lv99: ['fas fa-star', 'epic', 'game', 100],
  bio: ['fas fa-flask', 'rare', 'game', 50],
  war: ['fas fa-trophy', 'epic', 'game', 100],
  top10: ['fas fa-ranking-star', 'legend', 'game', 150],
  bug: ['fas fa-bug', 'rare', 'event', 50],
  founder: ['fas fa-gem', 'legend', 'event', 100],
  event: ['fas fa-medal', 'epic', 'event', 100]
};
export const ACH_TOTAL = Object.keys(ACH).length;
export const TIER_COLOUR = {
  newcomer: 'bronze',
  adventurer: 'silver',
  veteran: 'gold',
  champion: 'rune',
  legend: 'lantern'
};
export const badgeStyle = () => app.forum.attribute('aetBadgeStyle') === 'shield' ? 'shield' : 'roundel';
export const gamifyOf = user => (user && user.attribute ? user.attribute('aetGamify') : null) || null;
export function fmtDate(unix, opts = {
  day: 'numeric',
  month: 'short',
  year: 'numeric'
}) {
  try {
    return new Date(unix * 1000).toLocaleDateString(app.data.locale || 'en', opts);
  } catch (e) {
    return new Date(unix * 1000).toISOString().slice(0, 10);
  }
}

// ------------------------------------------------------------------ level emblems

/** Variant 1: the gold-ringed numeric roundel (sizes: sm 24, md 32, xl 84 with "LEVEL"). */
export function Roundel(g, size = 'md', extra = '') {
  const tier = TIER_COLOUR[g.tier] || 'bronze';
  return m("span", {
    className: `aet-lv1 aet-lv1--${size} aet-t-${tier} ${extra}`,
    role: "img",
    "aria-label": ts('level_aria', {
      level: g.lv
    })
  }, size === 'xl' ? m("small", null, ts('level_word')) : null, m("b", null, g.lv));
}
let shieldId = 0;
/** Variant 2: the shield (sizes md 28x33, xl 78x92). */
export function Shield(g, size = 'md', extra = '') {
  const tier = TIER_COLOUR[g.tier] || 'bronze';
  const id = `aet-sg${++shieldId}`;
  const inner = 'M14 3.6 23.8 6.7v9c0 6.2-4 10.6-9.8 13.5C8.2 26.3 4.2 21.9 4.2 15.7v-9z';
  return m("span", {
    className: `aet-lv2 aet-lv2--${size} aet-t-${tier} ${extra}`,
    role: "img",
    "aria-label": ts('level_aria', {
      level: g.lv
    })
  }, m("svg", {
    viewBox: "0 0 28 33",
    "aria-hidden": "true"
  }, m("defs", null, m("linearGradient", {
    id: id,
    x1: "0",
    y1: "0",
    x2: "0",
    y2: "1"
  }, m("stop", {
    offset: "0",
    "stop-color": "var(--ring-a)"
  }), m("stop", {
    offset: ".5",
    "stop-color": "var(--ring-b)"
  }), m("stop", {
    offset: "1",
    "stop-color": "var(--ring-c)"
  }))), m("path", {
    d: "M14 1.2 26 5v10.6c0 7.6-5 12.8-12 16.2C7 28.4 2 23.2 2 15.6V5z",
    fill: `url(#${id})`
  }), m("path", {
    d: inner,
    fill: "#14162e"
  }), m("path", {
    d: inner,
    fill: "none",
    stroke: "rgba(255,255,255,.1)"
  })), m("b", null, g.lv));
}
export const Emblem = (g, size = 'md', extra = '') => badgeStyle() === 'shield' ? Shield(g, size, extra) : Roundel(g, size, extra);

/** The level tooltip: "Level 24 · Veteran", how it is earned, the bar, "389 / 548 EXP" and "159 to Lv 25". */
export function LevelTip(g) {
  const max = g.lv >= 50;
  return m("span", {
    className: "aet-tip aet-tip--level",
    role: "tooltip"
  }, m("span", {
    className: "aet-tip__h"
  }, ts('level_n', {
    level: g.lv
  }), " \xB7 ", ts(`tier.${g.tier}`)), m("span", {
    className: "aet-tip__meta"
  }, t('tip_earned')), ExpBar(g.pct, 'lg'), m("span", {
    className: "aet-tip__meta aet-tip__row"
  }, max ? m("span", null, t('max_level')) : [m("span", null, ts('exp_of', {
    into: nf(g.into),
    need: nf(g.need)
  })), m("span", null, ts('to_next', {
    count: nf(g.toNext),
    level: g.lv + 1
  }))]));
}

/** An emblem with its tooltip on hover / keyboard focus. */
export function EmblemWithTip(g, size = 'md', extra = '') {
  return m("span", {
    className: `aet-tipwrap aet-tipwrap--level ${extra}`,
    tabindex: "0"
  }, Emblem(g, size), LevelTip(g));
}

// ------------------------------------------------------------------ EXP bar

export function ExpBar(pct, size = '', label = false) {
  const p = Math.max(0, Math.min(100, Number(pct) || 0));
  return m("span", {
    className: `aet-xp ${size ? `aet-xp--${size}` : ''}`
  }, label ? m("span", {
    className: "aet-xp__label"
  }, t('exp')) : null, m("span", {
    className: "aet-xp__bar",
    role: "progressbar",
    "aria-valuemin": "0",
    "aria-valuemax": "100",
    "aria-valuenow": p
  }, m("i", {
    style: {
      width: `${p}%`
    }
  })), label ? m("span", {
    className: "aet-xp__num"
  }, p, "%") : null);
}

// ------------------------------------------------------------------ game chip

const glyph = (body, size) => m.trust(`<svg viewBox="0 0 24 24" width="${size}" height="${size}" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false">${body}</svg>`);
/** The website's class glyphs (web/src/views/game.ts CLASS_GLYPHS): job % 4, Lycan 8. */
const CLASS = {
  warrior: '<path d="M19 3h2v2L10 16l-2-2z"/><path d="M6 13l5 5"/><path d="M4.5 19.5l3-3"/><path d="M3 21l1.5-1.5"/>',
  ninja: '<path d="M6 3c8 1 14 7 15 15"/><path d="M6 3l15 15"/><path d="M3 21l7-7"/><path d="M3 21h3"/><path d="M3 21v-3"/>',
  sura: '<path d="M12 3c1 4 6 5 6 11a6 6 0 0 1-12 0c0-3 2-4 3-6 0 2 1 3 2 3 0-3-1-5 1-8z"/>',
  shaman: '<circle cx="12" cy="12" r="3"/><path d="M12 3v4"/><path d="M12 17v4"/><path d="M3 12h4"/><path d="M17 12h4"/><path d="M6 6l2.5 2.5"/><path d="M15.5 15.5L18 18"/><path d="M18 6l-2.5 2.5"/><path d="M8.5 15.5L6 18"/>',
  lycan: '<path d="M6 20c2-5 2-11 1-16"/><path d="M11 20c2-5 2-11 1-16"/><path d="M16 20c2-5 2-11 1-16"/>'
};
export const classKind = job => job === 8 ? 'lycan' : ['warrior', 'ninja', 'sura', 'shaman'][((job || 0) % 4 + 4) % 4];
export const classGlyph = (job, size = 13) => glyph(CLASS[classKind(job)], size);
export function jobName(job) {
  const key = `aethernal-forum.forum.gamify.job.${job}`;
  return app.translator.translations[key] ? ts(`job.${job}`) : ts('job.unknown');
}
export const empName = emp => emp >= 1 && emp <= 3 ? ts(`emp.${emp}`) : '';
export function Flag(emp) {
  if (!(emp >= 1 && emp <= 3)) return null;
  return m("svg", {
    className: `aet-flag-k aet-flag-k--${emp}`,
    viewBox: "0 0 20 14",
    role: "img",
    "aria-label": empName(emp)
  }, m("title", null, empName(emp)), m("path", {
    d: "M1.5 1h17l-3.2 6 3.2 6h-17z"
  }));
}

/** The guild's mark from the website, else a crest in colours derived from its name. */
export function Crest(guild) {
  if (!guild) return null;
  if (guild.mark) {
    return m("img", {
      className: "aet-gmark",
      src: `${site()}${guild.mark}`,
      alt: "",
      width: "16",
      height: "12",
      loading: "lazy"
    });
  }
  let h = 0;
  for (const ch of guild.name || '') h = h * 31 + ch.charCodeAt(0) >>> 0;
  const a = h % 360;
  const b = (a + 40 + (h >> 8) % 80) % 360;
  return m("span", {
    className: "aet-gcrest",
    style: {
      background: `linear-gradient(135deg,hsl(${a} 60% 42%),hsl(${b} 75% 62%))`
    }
  });
}

/** "Lv 87 · Warrior · [flag] · [mark] Guild" (+ the character name when its owner shows it). */
export function Chip(chip, extra = '') {
  if (!chip) return null;
  const dot = m("span", {
    className: "aet-chip__dot"
  }, "\xB7");
  return m("span", {
    className: `aet-chip ${extra}`
  }, m("span", {
    className: `aet-chip__cls aet-cls--${classKind(chip.job)}`
  }, classGlyph(chip.job)), chip.name ? [m("span", {
    className: "aet-chip__name"
  }, chip.name), dot] : null, m("span", {
    className: "aet-chip__lv"
  }, ts('lv', {
    level: chip.lv
  })), dot, m("span", null, jobName(chip.job)), chip.emp ? [dot, Flag(chip.emp)] : null, chip.guild ? [dot, Crest(chip.guild), m("span", {
    className: "aet-chip__guild"
  }, chip.guild.name)] : null);
}

// ------------------------------------------------------------------ medals

export const achName = key => ts(`ach.${key}.name`);
export function MedalIcon(key, extra = '') {
  const a = ACH[key];
  if (!a) return null;
  return m("span", {
    className: `aet-medal aet-r-${a[1]} ${extra}`,
    "aria-hidden": "true"
  }, m("i", {
    className: `icon ${a[0]}`
  }));
}
export function MedalTip(key, at) {
  const a = ACH[key];
  const rate = (app.forum.attribute('aetRates') || {})[key];
  return m("span", {
    className: `aet-tip aet-tip--medal aet-r-${a[1]}`,
    role: "tooltip"
  }, m("span", {
    className: "aet-tip__h"
  }, m("i", {
    className: `icon ${a[0]}`,
    "aria-hidden": "true"
  }), achName(key)), m("span", {
    className: "aet-tip__rar"
  }, ts(`rarity.${a[1]}`), " \xB7 ", ts(`group.${a[2]}`)), m("span", {
    className: "aet-tip__p"
  }, t(`ach.${key}.desc`)), at ? m("span", {
    className: "aet-tip__meta"
  }, ts('medal_meta', {
    date: fmtDate(at),
    exp: a[3],
    pct: rate ?? 0
  })) : null);
}

/** One medal with its tooltip (hover / focus). */
export function Medal(key, at) {
  if (!ACH[key]) return null;
  return m("span", {
    className: "aet-tipwrap aet-tipwrap--medal",
    tabindex: "0",
    "aria-label": achName(key)
  }, MedalIcon(key), MedalTip(key, at));
}

/** Up to `max` medals + "+N". */
export function Medals(list, max = 3, total = null) {
  const items = (list || []).filter(md => ACH[md[0]]);
  const n = total ?? items.length;
  if (!n) return null;
  return m("span", {
    className: "aet-medals"
  }, items.slice(0, max).map(md => Medal(md[0], md[1])), n > max ? m("span", {
    className: "aet-medals__more"
  }, "+", n - max) : null);
}

/** A small helper for the API routes (Flarum's request with the session + CSRF token). */
export function api(method, path, body) {
  return app.request({
    method,
    url: `${app.forum.attribute('apiUrl')}${path}`,
    body
  });
}
flarum.reg.add('aethernal-forum', 'forum/gamify/ui', { t: t,ts: ts,site: site,nf: nf,ACH: ACH,ACH_TOTAL: ACH_TOTAL,TIER_COLOUR: TIER_COLOUR,badgeStyle: badgeStyle,gamifyOf: gamifyOf,fmtDate: fmtDate,Roundel: Roundel,Shield: Shield,Emblem: Emblem,LevelTip: LevelTip,EmblemWithTip: EmblemWithTip,ExpBar: ExpBar,classKind: classKind,classGlyph: classGlyph,jobName: jobName,empName: empName,Flag: Flag,Crest: Crest,Chip: Chip,achName: achName,MedalIcon: MedalIcon,MedalTip: MedalTip,Medal: Medal,Medals: Medals,api: api, });